import {
  useEffect, useRef, useState, useCallback,
} from 'react';
import { navigate } from 'gatsby';
import Swagger from 'swagger-client';
import { createUseStyles } from 'react-jss';
import { breakpoint } from 'src/theme';
import { IntentionalAny, PlanDetails } from './types';
import { navbarHeightPx } from './components/Navbar/utils';

export function useConfig() {
  const planSplitRegex = /,\s*/g;
  return {
    appRoot: process.env.GATSBY_APP_ROOT as string,
    apiRoot: process.env.GATSBY_API_ROOT as string,
    planIds: {
      premium: (process.env.GATSBY_PREMIUM_PLANS as string).split(planSplitRegex),
      elite: (process.env.GATSBY_ELITE_PLANS as string).split(planSplitRegex),
      exclusive: (process.env.GATSBY_EXCLUSIVE_PLANS as string).split(planSplitRegex),
      custom: (process.env.GATSBY_CUSTOM_PLANS as string).split(planSplitRegex),
    },
  };
}

export function useInitializeSwagger(apiServices: ApiService[] = [
  'auth-api',
  'home-api',
  'billing-api',
]) {
  const [isInitialized, setIsInitialized] = useState(false);
  const { apiRoot } = useConfig();
  useEffect(() => {
    window.apis = {};
    Promise.all(apiServices.map(async (service) => {
      try {
        const serviceClient = await new Swagger({
          url: `${apiRoot}/${service}/swagger.json`,
          requestInterceptor: (req: IntentionalAny) => {
            // our APIs only consume application/json, and this header needs to be specified
            // for some endpoints since this Swagger client ignores the default content type
            // in swagger.json
            if (!req.headers['Content-Type']) {
              req.headers['Content-Type'] = 'application/json';
            }
          },
        });
        window.apis[service] = serviceClient.apis;
      } catch (err) {
        // Intentionally empty
      }
    })).then(() => setIsInitialized(true));
  // We have no reason to change apiServices dynamically,
  // and adding it as a dependency would mean we have to memoize it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  Swagger.http.withCredentials = true;
  return isInitialized;
}

export const useBreakpointToggleStyles = createUseStyles({
  mobileOnly: {
    [breakpoint.desktop()]: {
      display: 'none !important',
    },
  },
  desktopOnly: {
    [breakpoint.mobile()]: {
      display: 'none !important',
    },
  },
  upToSm: {
    [breakpoint.up('sm')]: {
      display: 'none !important',
    },
  },
  aboveSm: {
    [breakpoint.down('sm')]: {
      display: 'none !important',
    },
  },
  upToMd: {
    [breakpoint.up('md')]: {
      display: 'none !important',
    },
  },
  aboveMd: {
    [breakpoint.down('md')]: {
      display: 'none !important',
    },
  },
  upToLg: {
    [breakpoint.up('lg')]: {
      display: 'none !important',
    },
  },
  aboveLg: {
    [breakpoint.down('lg')]: {
      display: 'none !important',
    },
  },
  upToXl: {
    [breakpoint.up('xl')]: {
      display: 'none !important',
    },
  },
  aboveXl: {
    [breakpoint.down('xl')]: {
      display: 'none !important',
    },
  },
});

export function usePlanDetails(): PlanDetails[] {
  const config = useConfig();
  const freeFeatures = [
    'Notifications Feed',
    'Task Management App',
    'Custom Website & Editor',
    'SSL-secured Subdomain',
    'Customer List (CRM)',
    '24/7/365 Email Support',
    '1 Team Account',
  ];
  const premiumFeatures = [
    'Notifications Feed',
    'Task Management App',
    'Custom Website & Editor',
    '.com Website Domain Included',
    'Complimentary SSL Certificate',
    'Search Engine Optimization (SEO)',
    'Customer List (CRM)',
    'Basic Ecommerce',
    'Invoicing App',
    'Calendar App',
    'Up to 5 Team Accounts',
    'White Glove Migration Service',
    'Phone Support',
  ];
  const eliteFeatures = [
    'Notifications Feed',
    'Task Management App',
    'Custom Website & Editor',
    '.com Website Domain Included',
    'Complimentary SSL Certificate',
    'Search Engine Optimization (SEO)',
    'Customer List (CRM)',
    'Ecommerce & Payments Processing',
    'Invoicing App',
    'Calendar App',
    'Up to 10 Team Accounts',
    'White Glove Migration Service',
    'Dedicated Phone Support',
    'Dedicated Business Advisor',
    'Early Access To New Releases',
  ];
  const exclusiveFeatures = [
    'Notifications Feed',
    'Task Management App',
    'Custom Website & Editor',
    '.com Website Domain Included',
    'Complimentary SSL Certificate',
    'Search Engine Optimization (SEO)',
    'Customer List (CRM)',
    'Invoicing App',
    'Calendar App',
    'Up to 10 Team Accounts',
    'White Glove Migration Service',
    'Dedicated Phone Support',
    'Social Media Management',
    'Ads Management',
    'Ecommerce & Payments Processing',
    'Content Creation',
    'Blog writing, copywriting',
    'Dedicated Business Advisor',
    'Early Access To New Releases',
  ];
  const customFeatures = [
    'Notifications Feed',
    'Task Management App',
    'Custom Website & Editor',
    '.com Website Domain Included',
    'Complimentary SSL Certificate',
    'Search Engine Optimization (SEO)',
    'Customer List (CRM)',
    'Invoicing App',
    'Calendar App',
    'Unlimited Team Accounts',
    'White Glove Migration Service',
    '24/7 Phone Support',
    'Social Media Management',
    'Ads Management',
    'Ecommerce & Payments Processing',
    'Content Creation',
    'Blog writing, copywriting',
    'Dedicated Business Advisor',
    'Early Access To New Releases',
  ];

  const monthlyPremiumIds = config.planIds.premium.filter((id) => id.includes('premium'));
  const annualPremiumIds = config.planIds.premium.filter((id) => !id.includes('premium'));
  const monthlyEliteIds = config.planIds.elite.filter((id) => id.includes('elite'));
  const annualEliteIds = config.planIds.elite.filter((id) => !id.includes('elite'));
  const monthlyExclusiveIds = config.planIds.exclusive.filter((id) => id.includes('exclusive'));
  const annualExclusiveIds = config.planIds.exclusive.filter((id) => !id.includes('exclusive'));
  const monthlyCustomIds = config.planIds.custom.filter((id) => id.includes('custom'));
  const annualCustomIds = config.planIds.custom.filter((id) => !id.includes('custom'));

  return [
    {
      title: 'Free Plan',
      recurringCostCents: 0,
      features: freeFeatures,
      planIds: ['free'],
      hidden: false,
      interval: 'month',
    },
    {
      title: 'Free Plan',
      recurringCostCents: 0,
      features: freeFeatures,
      planIds: ['free'],
      hidden: false,
      interval: 'year',
    },
    {
      title: 'Premium Plan',
      recurringCostCents: 7900,
      features: premiumFeatures,
      planIds: monthlyPremiumIds,
      hidden: false,
      interval: 'month',
    },
    {
      title: 'Premium Plan',
      recurringCostCents: 6800,
      features: premiumFeatures,
      planIds: annualPremiumIds,
      hidden: false,
      interval: 'year',
    },
    {
      title: 'Elite Plan',
      recurringCostCents: 24900,
      features: eliteFeatures,
      planIds: monthlyEliteIds,
      hidden: false,
      interval: 'month',
    },
    {
      title: 'Elite Plan',
      recurringCostCents: 21200,
      features: eliteFeatures,
      planIds: annualEliteIds,
      hidden: false,
      interval: 'year',
    },
    {
      title: 'Custom Plan',
      recurringCostCents: 99900,
      features: exclusiveFeatures,
      planIds: monthlyExclusiveIds,
      hidden: true,
      interval: 'month',
    },
    {
      title: 'Custom Plan',
      recurringCostCents: 99900,
      features: exclusiveFeatures,
      planIds: annualExclusiveIds,
      hidden: true,
      interval: 'year',
    },
    {
      title: 'Custom Plan',
      recurringCostCents: 999900,
      features: customFeatures,
      planIds: monthlyCustomIds,
      hidden: true,
      interval: 'month',
    },
    {
      title: 'Custom Plan',
      recurringCostCents: 999900,
      features: customFeatures,
      planIds: annualCustomIds,
      hidden: true,
      interval: 'year',
    },
  ];
}

export function useQueryParam(key: string): string | null | undefined {
  const [queryParam, setQueryParam] = useState<string | null>();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setQueryParam(params?.get(key));
  }, [key]);
  return queryParam;
}

/**
 * This seems like a useless hook, but it addresses hydration issues
 */
export function useSearch() {
  const [search, setSearch] = useState('');
  useEffect(() => {
    setSearch(window.location.search);
  }, []);
  return search;
}

export function useLocation() {
  const [location, setLocation] = useState<Location>();
  useEffect(() => {
    setLocation(window.location);
  }, []);
  return location;
}

export function usePlanId(): string | null | undefined {
  const { appRoot } = useConfig();
  const redirectPath = useQueryParam('redirect');
  const planId = useQueryParam('planId');

  if (planId) return planId;
  if (!redirectPath) return null;

  try {
    const url = new URL(redirectPath, appRoot);
    return url.searchParams.get('planId');
  } catch {
    return null;
  }
}

export function useScrollableRef() {
  const ref = useRef<HTMLDivElement>(null);
  const handleScroll = useCallback(() => {
    const boundingRect = ref.current?.getBoundingClientRect();
    if (boundingRect) {
      window.scrollTo({
        top: window.scrollY + boundingRect.top - navbarHeightPx, // account for fixed NavBar
        behavior: 'smooth',
      });
    }
  }, []);
  return {
    ref,
    handleScroll,
  };
}

export function useLink(route: string, search?: string) {
  const currentSearch = useSearch();
  const searchParams = new URLSearchParams(currentSearch);
  if (search) {
    const newSearchParams = new URLSearchParams(search);
    newSearchParams.forEach((value, key) => {
      searchParams.set(key, value);
    });
  }
  const newSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';

  const onNavigate = useCallback(() => {
    navigate(`${route}${newSearch}`);
  }, [route, newSearch]);

  return {
    href: `${route}${newSearch}`,
    onNavigate,
  };
}

export function useIsVisible(ref: React.RefObject<HTMLElement>) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting));
  }, []);

  useEffect(() => {
    if (ref.current) observerRef.current?.observe(ref.current);
    return () => {
      observerRef.current?.disconnect();
    };
  }, [ref]);

  return isVisible;
}

export function useScrollLock() {
  const lockScroll = useCallback(() => {
    document.body.style.overflow = 'hidden';
  }, []);

  const unlockScroll = useCallback(() => {
    document.body.style.overflow = '';
  }, []);

  useEffect(() => () => unlockScroll(), [unlockScroll]);

  return {
    lockScroll,
    unlockScroll,
  };
}

export function useSwipable({
  onSwipeLeft,
  onSwipeRight,
}: {
  onSwipeLeft: () => void,
  onSwipeRight: () => void,
}) {
  type Position = {
    x: number,
    y: number,
  }
  const [initialPos, setInitialPos] = useState<Position>({
    x: 0,
    y: 0,
  });
  const [finalPos, setFinalPos] = useState<Position>({
    x: 0,
    y: 0,
  });

  const handleTouchMove: React.TouchEventHandler<HTMLDivElement> = useCallback((e) => {
    setFinalPos({
      x: e.nativeEvent.touches[0].clientX,
      y: e.nativeEvent.touches[0].clientY,
    });
  }, []);

  const handleTouchStart: React.TouchEventHandler<HTMLDivElement> = useCallback((e) => {
    setInitialPos({
      x: e.nativeEvent.touches[0].clientX,
      y: e.nativeEvent.touches[0].clientY,
    });
  }, []);

  const handleTouchEnd: React.TouchEventHandler<HTMLDivElement> = useCallback(() => {
    if (initialPos.x < finalPos.x - 50 && Math.abs(initialPos.y - finalPos.y) < 50) {
      onSwipeLeft();
    }
    if (initialPos.x > finalPos.x + 50 && Math.abs(initialPos.y - finalPos.y) < 50) {
      onSwipeRight();
    }
  }, [onSwipeLeft, onSwipeRight, initialPos, finalPos]);

  const handleTouchCancel: React.TouchEventHandler<HTMLDivElement> = useCallback(() => {
    setInitialPos({
      x: 0,
      y: 0,
    });
    setFinalPos({
      x: 0,
      y: 0,
    });
  }, []);

  return {
    handleTouchMove,
    handleTouchStart,
    handleTouchEnd,
    handleTouchCancel,
  };
}
